import { Box, Button, CircularProgress, Dialog, DialogContent, IconButton, Tooltip, Typography } from '@mui/material'
import {
  Close as CloseIcon,
  Download as DownloadIcon,
  Fullscreen as FullscreenIcon,
  FullscreenExit as FullscreenExitIcon,
  InsertDriveFile as FileIcon,
} from '@mui/icons-material'
import { useEffect, useRef, useState } from 'react'
import axios from 'axios'
import { MAX_PREVIEW_FILE_SIZE_BYTES, getFileExtension } from '../../../../utility/fileUtil'

const NODE_BASE_URL = process.env.REACT_APP_NODE_BASE_URL
const OFFICE_ONLINE_EMBED = 'https://view.officeapps.live.com/op/embed.aspx?src='

/** Explicit MIME types so blob URLs render correctly in the browser */
const MIME_TYPES = {
  pdf:  'application/pdf',
  png:  'image/png',
  jpg:  'image/jpeg',
  jpeg: 'image/jpeg',
  gif:  'image/gif',
  bmp:  'image/bmp',
  tif:  'image/tiff',
  tiff: 'image/tiff',
  heic: 'image/heic',
  csv:  'text/csv',
  txt:  'text/plain',
  html: 'text/html',
  htm:  'text/html',
}

const getMimeType = (ext) => MIME_TYPES[ext] || 'application/octet-stream'

// ── Rendering strategy groups ────────────────────────────────────────────────

/** Needs a public presigned URL → Microsoft Office Online iframe */
const OFFICE_EXTENSIONS = new Set(['xlsx', 'xlsm', 'xls', 'docx', 'doc', 'pptx', 'ppt'])

/** Native browser PDF renderer via <iframe> */
const PDF_EXTENSIONS = new Set(['pdf'])

/** Rendered as <img> tag */
const IMAGE_EXTENSIONS = new Set(['png', 'jpg', 'jpeg', 'gif', 'bmp', 'tif', 'tiff', 'heic'])

/** Fetched as text → displayed in <pre> */
const TEXT_EXTENSIONS = new Set(['csv', 'txt'])

/** Rendered in a sandboxed <iframe> as blob URL */
const HTML_EXTENSIONS = new Set(['html', 'htm'])

/**
 * Returns which strategy to use for a given extension.
 * Returns null for unsupported types.
 */
const getStrategy = (ext) => {
  if (OFFICE_EXTENSIONS.has(ext)) return 'office'
  if (PDF_EXTENSIONS.has(ext)) return 'pdf'
  if (IMAGE_EXTENSIONS.has(ext)) return 'image'
  if (TEXT_EXTENSIONS.has(ext)) return 'text'
  if (HTML_EXTENSIONS.has(ext)) return 'html'
  return null // unsupported — show download message
}

// ── Sub-renderers ─────────────────────────────────────────────────────────────

const OfficePreview = ({ url }) => (
  <iframe
    title="Office Document Preview"
    src={`${OFFICE_ONLINE_EMBED}${encodeURIComponent(url)}`}
    style={{ flex: 1, width: '100%', minHeight: '78vh', border: 'none' }}
  />
)

const PdfPreview = ({ url }) => (
  <iframe
    title="PDF Preview"
    src={url}
    style={{ flex: 1, width: '100%', minHeight: '78vh', border: 'none' }}
    type="application/pdf"
  />
)

const ImagePreview = ({ url }) => (
  <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2, overflow: 'auto' }}>
    <img src={url} alt="Preview" style={{ maxWidth: '100%', maxHeight: '76vh', objectFit: 'contain' }} />
  </Box>
)

const TextPreview = ({ content }) => (
  <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
    <pre style={{ margin: 0, fontSize: 13, fontFamily: 'monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
      {content}
    </pre>
  </Box>
)

const HtmlPreview = ({ url }) => (
  <iframe
    title="HTML Preview"
    src={url}
    sandbox="allow-same-origin"
    style={{ flex: 1, width: '100%', minHeight: '78vh', border: 'none' }}
  />
)

const UnsupportedPreview = ({ ext, onDownload }) => (
  <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2, p: 4 }}>
    <FileIcon sx={{ fontSize: 64, color: 'text.disabled' }} />
    <Typography variant="h6" color="text.secondary">
      Preview not available for .{ext} files
    </Typography>
    <Typography variant="body2" color="text.secondary" align="center">
      Please download the file and open it in the appropriate viewer.
    </Typography>
    {onDownload && (
      <Button variant="contained" startIcon={<DownloadIcon />} onClick={onDownload}>
        Download File
      </Button>
    )}
  </Box>
)

// ── Main Component ────────────────────────────────────────────────────────────

const DocViewerPreview = ({ open, onClose, fileKey, user, onDownload }) => {
  const [state, setState] = useState({ loading: false, error: null, tooLarge: false })
  const [blobUrl, setBlobUrl] = useState(null)       // pdf / image / text / html
  const [officeUrl, setOfficeUrl] = useState(null)   // office online presigned URL
  const [textContent, setTextContent] = useState('') // csv / txt
  const blobUrlRef = useRef(null)

  const ext = getFileExtension(fileKey)
  const strategy = getStrategy(ext)

  useEffect(() => {
    if (!open || !fileKey) return

    let cancelled = false

    // Revoke previous blob URL
    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current)
      blobUrlRef.current = null
    }
    setBlobUrl(null)
    setOfficeUrl(null)
    setTextContent('')
    setState({ loading: true, error: null, tooLarge: false })

    const fileName = fileKey.split('/').pop()
    const folder = fileKey.includes('/') ? fileKey.split('/')[0] : 'files'
    const authHeader = { Authorization: `Bearer ${user.token}` }

    const load = async () => {
      try {
        // 1. Skip size check for unsupported types — just show message immediately
        if (!strategy) {
          if (!cancelled) setState({ loading: false, error: null, tooLarge: false })
          return
        }

        // 2. Check file size
        const sizeRes = await axios.get(
          `${NODE_BASE_URL}/data-entry/file/${encodeURIComponent(fileName)}/size`,
          { headers: authHeader, params: { folder } }
        )
        const fileSize = sizeRes.data?.size ?? 0
        if (!cancelled && fileSize > MAX_PREVIEW_FILE_SIZE_BYTES) {
          setState({ loading: false, error: null, tooLarge: true })
          return
        }

        if (strategy === 'office') {
          // Office Online needs a publicly accessible presigned URL
          const urlRes = await axios.get(
            `${NODE_BASE_URL}/data-entry/file/signed-url/${encodeURIComponent(fileName)}`,
            { headers: authHeader, params: { folder } }
          )
          if (!cancelled) {
            setOfficeUrl(urlRes.data?.url)
            setState({ loading: false, error: null, tooLarge: false })
          }
        } else {
          // All other supported types: fetch as blob through backend (no S3 CORS)
          const fileRes = await axios.get(
            `${NODE_BASE_URL}/data-entry/file/${encodeURIComponent(fileName)}`,
            { headers: authHeader, params: { folder }, responseType: 'blob' }
          )
          if (!cancelled) {
            if (strategy === 'text') {
              // Read blob as text directly
              const text = await fileRes.data.text()
              setTextContent(text)
            } else {
              // Re-wrap with explicit MIME type — backend may return octet-stream
              const typedBlob = new Blob([fileRes.data], { type: getMimeType(ext) })
              const url = URL.createObjectURL(typedBlob)
              blobUrlRef.current = url
              setBlobUrl(url)
            }
            setState({ loading: false, error: null, tooLarge: false })
          }
        }
      } catch (err) {
        if (!cancelled) setState({ loading: false, error: err.message || 'Failed to load preview', tooLarge: false })
      }
    }

    load()
    return () => { cancelled = true }
  }, [open, fileKey, user])

  // Revoke blob URL when dialog closes
  useEffect(() => {
    if (!open && blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current)
      blobUrlRef.current = null
      setBlobUrl(null)
    }
  }, [open])

  const { loading, error, tooLarge } = state
  const paperRef = useRef(null)
  const [isFullscreen, setIsFullscreen] = useState(false)

  useEffect(() => {
    const handler = () => setIsFullscreen(!!document.fullscreenElement)
    document.addEventListener('fullscreenchange', handler)
    return () => document.removeEventListener('fullscreenchange', handler)
  }, [])

  const handleFullscreen = () => {
    const el = paperRef.current
    if (!el) return
    document.fullscreenElement ? document.exitFullscreen() : el.requestFullscreen?.()
  }

  const fileName = fileKey ? fileKey.split('/').pop() : ''

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        ref: paperRef,
        sx: { height: '95vh', display: 'flex', flexDirection: 'column' },
      }}
    >
      {/* ── Toolbar ── */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 1.5,
          py: 0.75,
          borderBottom: '1px solid #e0e0e0',
          backgroundColor: '#fafafa',
          flexShrink: 0,
          gap: 1,
        }}
      >
        {/* Left: filename */}
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: 600,
            color: '#444',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            flex: 1,
            minWidth: 0,
          }}
        >
          {fileName || 'Preview'}
        </Typography>

        {/* Right: actions */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
          {onDownload && (
            <Tooltip title="Download">
              <IconButton size="small" onClick={onDownload}>
                <DownloadIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title={isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}>
            <IconButton size="small" onClick={handleFullscreen}>
              {isFullscreen ? <FullscreenExitIcon fontSize="small" /> : <FullscreenIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
          <Tooltip title="Close">
            <IconButton size="small" onClick={onClose}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
      <DialogContent sx={{ p: 0, minHeight: '80vh', display: 'flex', flexDirection: 'column' }}>

        {loading && (
          <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', flex: 1, gap: 2, minHeight: 400 }}>
            <CircularProgress color="primary" />
            <Typography variant="body2" color="text.secondary">Loading preview…</Typography>
          </Box>
        )}

        {!loading && tooLarge && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flex: 1, gap: 2, p: 4 }}>
            <Typography variant="h6" color="text.secondary">File is too large to preview</Typography>
            <Typography variant="body2" color="text.secondary">
              Files larger than {Math.round(MAX_PREVIEW_FILE_SIZE_BYTES / (1024 * 1024))} MB cannot be previewed in the browser.
            </Typography>
            {onDownload && (
              <Button variant="contained" startIcon={<DownloadIcon />} onClick={onDownload}>Download File</Button>
            )}
          </Box>
        )}

        {!loading && error && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flex: 1, gap: 2, p: 4 }}>
            <Typography variant="body1" color="error">Could not load preview. Please download the file instead.</Typography>
            {onDownload && (
              <Button variant="outlined" startIcon={<DownloadIcon />} onClick={onDownload}>Download File</Button>
            )}
          </Box>
        )}

        {/* Unsupported type */}
        {!loading && !error && !tooLarge && !strategy && (
          <UnsupportedPreview ext={ext} onDownload={onDownload} />
        )}

        {/* Office Online */}
        {!loading && !error && !tooLarge && strategy === 'office' && officeUrl && (
          <OfficePreview url={officeUrl} />
        )}

        {/* Native PDF */}
        {!loading && !error && !tooLarge && strategy === 'pdf' && blobUrl && (
          <PdfPreview url={blobUrl} />
        )}

        {/* Images */}
        {!loading && !error && !tooLarge && strategy === 'image' && blobUrl && (
          <ImagePreview url={blobUrl} />
        )}

        {/* CSV / TXT */}
        {!loading && !error && !tooLarge && strategy === 'text' && (
          <TextPreview content={textContent} />
        )}

        {/* HTML */}
        {!loading && !error && !tooLarge && strategy === 'html' && blobUrl && (
          <HtmlPreview url={blobUrl} />
        )}

      </DialogContent>
    </Dialog>
  )
}

export default DocViewerPreview
